/**
 * Web Push subscription plumbing (ADR-034).
 *
 * Pure, React-free helpers that `use-browser-notifications.ts` composes into
 * the existing single `browserNotificationsEnabled` toggle. The service
 * worker (`public/sw.js`) handles delivery; these helpers handle the
 * subscribe / unsubscribe / register lifecycle on the app side and the two
 * authenticated API calls (fetch the VAPID key, POST / DELETE the
 * subscription).
 *
 * Everything here guards on capability — `'serviceWorker' in navigator &&
 * 'PushManager' in window` — so an old browser, an SSR pass, or an
 * insecure-context (non-https, non-localhost) page degrades to a no-op
 * rather than throwing. The foreground `new Notification(...)` path in the
 * hook stays intact regardless; push only LAYERS on top.
 */

import { apiClient } from '@/api/client'
import type { SubscribePushInput, VapidPublicKeyDto } from '@/api/types-bridge'

const SW_URL = '/sw.js'

/** True when the browser can register a service worker AND do Web Push. */
export function supportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof window.PushManager !== 'undefined'
  )
}

/**
 * Decode a URL-safe base64 VAPID public key into the `Uint8Array` that
 * `pushManager.subscribe({ applicationServerKey })` requires. The standard
 * incantation: pad to a multiple of 4, swap the URL-safe alphabet back to
 * standard base64, `atob`, then copy char codes into a byte array.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  // Back the view with a concrete ArrayBuffer (not the default ArrayBufferLike)
  // so it satisfies the `BufferSource` shape `applicationServerKey` expects.
  const buffer = new ArrayBuffer(rawData.length)
  const outputArray = new Uint8Array(buffer)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Register the push service worker at root scope (idempotent — the browser
 * returns the existing registration if `/sw.js` is already registered).
 * Returns `null` when the environment can't do push.
 */
export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!supportsPush()) return null
  return navigator.serviceWorker.register(SW_URL)
}

/** Fetch the stage VAPID public key from the BE (authenticated). */
async function fetchVapidPublicKey(): Promise<string> {
  // BE wraps every response in `{ data: ... }`. Unwrap to the key string.
  const res = await apiClient<{ data: VapidPublicKeyDto }>('/api/push/vapid-public-key')
  return res.data.publicKey
}

/** POST a browser `PushSubscription` to the BE (upsert on (user, endpoint)). */
async function postSubscription(subscription: PushSubscription): Promise<void> {
  // `PushSubscription.toJSON()` yields `{ endpoint, keys: { p256dh, auth },
  // expirationTime }`. The BE schema accepts `{ endpoint, keys, userAgent? }`
  // and ignores extras, but we shape the body explicitly so a future schema
  // tightening doesn't silently drop fields.
  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    // A subscription with no keys can't receive encrypted payloads; skip the
    // POST rather than register a dead endpoint server-side.
    return
  }
  const body: SubscribePushInput = {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  }
  await apiClient('/api/me/push-subscriptions', { method: 'POST', body })
}

/** DELETE a subscription endpoint from the BE (idempotent server-side). */
async function deleteSubscription(endpoint: string): Promise<void> {
  await apiClient('/api/me/push-subscriptions', {
    method: 'DELETE',
    body: { endpoint },
  })
}

/**
 * Subscribe this device to Web Push and register it server-side.
 *
 * Full flow: register SW → fetch VAPID key → reuse an existing
 * `pushManager` subscription if present, else `subscribe()` with the key →
 * POST the subscription to the BE. Best-effort: any step can fail (the BE
 * route 404s pre-deploy, the user could revoke permission mid-flow) and the
 * caller decides whether to surface that — the master `browserNotifications
 * Enabled` toggle is the source of truth, not subscription success.
 *
 * Idempotent: calling it when already subscribed re-POSTs the existing
 * subscription (cheap upsert), which is exactly the mount-time reconcile we
 * want for `pushsubscriptionchange` drift recovery.
 */
export async function subscribeToPush(): Promise<void> {
  if (!supportsPush()) return

  const registration = await registerPushServiceWorker()
  if (!registration) return

  // `pushManager` lives on the active registration; wait for it to be ready
  // so a just-registered SW doesn't race the subscribe call.
  const ready = await navigator.serviceWorker.ready

  const existing = await ready.pushManager.getSubscription()
  if (existing) {
    // Already subscribed in the browser — reconcile with the server.
    await postSubscription(existing)
    return
  }

  const vapidPublicKey = await fetchVapidPublicKey()
  const subscription = await ready.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  })
  await postSubscription(subscription)
}

/**
 * Unsubscribe this device from Web Push and remove it server-side.
 * Best-effort + idempotent: unsubscribes the `pushManager` subscription if
 * one exists and DELETEs its endpoint from the BE. A missing subscription is
 * a no-op.
 */
export async function unsubscribeFromPush(): Promise<void> {
  if (!supportsPush()) return

  const registration = await navigator.serviceWorker.getRegistration(SW_URL)
  if (!registration) return

  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  const { endpoint } = subscription
  // Unsubscribe the browser first; then tell the server. If the BE DELETE
  // fails (e.g. offline), the subscription is already gone client-side and
  // the BE prunes the stale row on the next push 404/410.
  await subscription.unsubscribe()
  await deleteSubscription(endpoint)
}

/**
 * Has this device got an active push subscription right now? Used by the
 * mount-time reconcile to decide whether to (re-)subscribe when permission is
 * granted + the server toggle is on but the browser dropped the subscription
 * (e.g. via `pushsubscriptionchange`).
 */
export async function hasActivePushSubscription(): Promise<boolean> {
  if (!supportsPush()) return false
  const registration = await navigator.serviceWorker.getRegistration(SW_URL)
  if (!registration) return false
  const subscription = await registration.pushManager.getSubscription()
  return subscription !== null
}
